import { UtensilsCrossed } from 'lucide-react';

import type { GuideModule } from '../types';

/**
 * `apps/web-admin/src/routes/menus/`: button labels and states quoted
 * verbatim from `menu-list-page.tsx`, `menu-detail-page.tsx`,
 * `menu-dietary-tags-tab.tsx` and `categories-page.tsx`. `requiredPermission`
 * mirrors `menu.read` in `layout/navigation.ts`.
 */
export const CATALOG_MODULE: GuideModule = {
  id: 'catalog',
  title: 'Catálogo',
  icon: UtensilsCrossed,
  requiredPermission: 'menu.read',
  priority: 5,
  summary:
    'El catálogo es la lista de todos los menús que se pueden ofrecer. Cada menú tiene un código, un precio base, un tipo de servicio (desayuno, almuerzo, vianda o cena), y puede tener etiquetas dietarias (por ejemplo sin TACC o vegano) y alérgenos declarados (por ejemplo maní o mariscos). Los tipos de servicio, las posiciones del menú, las etiquetas dietarias y los alérgenos se administran en una pantalla aparte llamada Categorías. Una posición del menú es un lugar fijo dentro de un tipo de servicio (por ejemplo "Almuerzo general" u "Opción de cena"): el Calendario, los pedidos y las plantillas se arman por posición, no eligiendo un menú libre cada vez. Cuando un menú deja de ofrecerse, se archiva en vez de eliminarse, para no perder el historial de pedidos y contratos que lo usaron.',
  steps: [
    {
      title: 'Crear un menú nuevo',
      description:
        'Andá a Catálogo y hacé clic en "Nuevo menú". Completá nombre, código, tipo de servicio y precio base, y guardá.',
      keyTerms: ['menu', 'tipo-de-servicio', 'precio-base'],
    },
    {
      title: 'Asignar etiquetas dietarias',
      description:
        'Entrá al detalle del menú, andá a la pestaña "Etiquetas dietarias", elegí una etiqueta del desplegable "Agregar etiqueta" y hacé clic en "Agregar".',
      keyTerms: ['etiqueta-dietaria'],
    },
    {
      title: 'Declarar los alérgenos de un menú',
      description:
        'Desde la pestaña "Alérgenos" del menú, sumá cada alérgeno que corresponda declarar.',
      keyTerms: ['alergeno'],
    },
    {
      title: 'Crear una categoría (tipo de servicio, posición, etiqueta o alérgeno)',
      description:
        'Andá a Catálogo, hacé clic en "Categorías" y, en la sección correspondiente, hacé clic en "Nuevo tipo de servicio", "Nueva posición", "Nueva etiqueta dietaria" o "Nuevo alérgeno" según lo que necesites cargar.',
      keyTerms: ['categoria'],
    },
    {
      title: 'Administrar las posiciones del menú',
      description:
        'En "Categorías", sección "Posiciones del menú", hacé clic en "Nueva posición", elegí su tipo de servicio y su orden de aparición. Si una posición deja de usarse, hacé clic en "Desactivar" en vez de borrarla: no hay acción de eliminar, igual que con los tipos de servicio. Una posición inactiva no aparece para cargar nuevas entradas de calendario ni en pedidos nuevos, pero conserva el historial de lo ya cargado.',
      keyTerms: ['posicion-del-menu'],
    },
    {
      title: 'Archivar un menú',
      description:
        'En el detalle del menú, hacé clic en "Archivar" y confirmá. Un menú archivado no se puede editar ni volver a ofrecer.',
      keyTerms: ['archivar'],
    },
  ],
  states: [
    {
      value: 'ACTIVE',
      label: 'Activo',
      description: 'El menú está disponible para asignarse a contratos y al calendario.',
    },
    {
      value: 'INACTIVE',
      label: 'Inactivo',
      description: 'El menú está temporalmente deshabilitado, pero no fue archivado.',
    },
    {
      value: 'ARCHIVED',
      label: 'Archivado',
      description: 'El menú fue retirado del catálogo de forma definitiva.',
    },
  ],
  faq: [
    {
      question: '¿Cuál es la diferencia entre desactivar y archivar un menú?',
      answer:
        'Desactivar es temporal: podés volver a activarlo. Archivar es definitivo: un menú archivado no se puede editar ni reactivar.',
    },
    {
      question: '¿Por qué no veo una etiqueta dietaria en el desplegable "Agregar etiqueta"?',
      answer:
        'Solo aparecen las etiquetas activas que todavía no están asignadas al menú. Si la etiqueta está inactiva, activala primero desde Categorías.',
    },
    {
      question: '¿Quién puede ver las notas de preparación de un menú?',
      answer:
        'Solo el personal con acceso a producción. Si no ves la pestaña "Notas de preparación", tu rol no tiene ese acceso.',
    },
    {
      question: '¿Puedo tener dos menús con el mismo código?',
      answer: 'No, el código de menú es único.',
    },
    {
      question: '¿Puedo eliminar una posición del menú que ya no uso?',
      answer:
        'No hay acción de eliminar, solo "Desactivar": lo mismo que con los tipos de servicio. Así no se pierde el historial de calendario, pedidos y plantillas que ya la usaron.',
    },
  ],
  tips: [
    'Declará todos los alérgenos relevantes antes de asignar el menú a un contrato: evita advertencias dietarias más adelante en el Calendario.',
    'Usá "Categorías" para revisar el orden de tipos de servicio antes de dar de alta un menú nuevo.',
    'Antes de archivar un menú, verificá que no tenga entradas de calendario futuras planificadas.',
    'Mantené el precio base actualizado: los contratos parten de ese valor para armar su propia lista de precios.',
  ],
};
